import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemePreference = "light" | "dark" | "device";
export type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "stock-assist-theme";
const THEME_CHOICES = new Set<ThemePreference>(["light", "dark", "device"]);

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const deviceThemeQuery =
  typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

function readInitialPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && THEME_CHOICES.has(stored as ThemePreference)) {
      return stored as ThemePreference;
    }
  } catch {
    // Storage can be unavailable in hardened browser contexts.
  }

  const initial = document.documentElement.dataset.themePreference;
  return initial && THEME_CHOICES.has(initial as ThemePreference)
    ? (initial as ThemePreference)
    : "device";
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "device") {
    return deviceThemeQuery?.matches ? "dark" : "light";
  }
  return preference;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readInitialPreference);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(readInitialPreference()),
  );

  // Reflect preference onto <html> and persist it.
  useEffect(() => {
    const resolved = resolveTheme(preference);
    document.documentElement.dataset.themePreference = preference;
    document.documentElement.dataset.theme = resolved;
    setResolvedTheme(resolved);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // The active theme still applies for this page when storage is unavailable.
    }
  }, [preference]);

  // Follow the OS theme while in device mode.
  useEffect(() => {
    if (!deviceThemeQuery) {
      return;
    }
    const handleChange = () => {
      if (preference === "device") {
        const resolved = deviceThemeQuery.matches ? "dark" : "light";
        document.documentElement.dataset.theme = resolved;
        setResolvedTheme(resolved);
      }
    };
    deviceThemeQuery.addEventListener("change", handleChange);
    return () => deviceThemeQuery.removeEventListener("change", handleChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(THEME_CHOICES.has(next) ? next : "device");
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
